/* eslint-disable max-lines */

import {
  ClassProvider,
  ConstructorProvider,
  ExistingProvider,
  FactoryProvider,
  Inject,
  Injectable,
  Injector,
  LOCALE_ID,
  Type,
  ValueProvider,
} from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { INITIAL_STATE, Store } from '@ngrx/store';
import { resolveMemberOrgGuid } from '@tellent/members-data';
import {
  ORG_GUID,
  TELLENT_CURRENT_USER,
  TELLENT_SUITE_ENABLED,
  TellentAuthTokenResponse,
  TellentCurrentUser,
  TellentMember,
  TellentUserResponse,
} from '@tellent/types';
import { BehaviorSubject, forkJoin, lastValueFrom, of, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';

import type { CurrentUser } from '@recruitee/account-types';
import { ANALYTICS_DISABLED } from '@recruitee/analytics';
import {
  ADMIN,
  APP_CONFIG,
  AUTH_METHOD,
  AUTH_TOKEN,
  COMPANY_ID,
  ENVIRONMENT,
  IAppConfig,
  PATH_WHITELIST,
} from '@recruitee/common';
import { DevFlagsHttpService, DEV_FLAGS, DevFlagsMap } from '@recruitee/dev-flags-data';
import { FEATURE_SPEC, FeatureSpecHttpClient } from '@recruitee/feature-spec-data';
import type { FeatureSpec } from '@recruitee/feature-spec-types';
import {
  AuthorisationStrategy,
  AuthService,
  GenericAuthService,
  HttpApiErrorHandlerService,
  HttpApiService,
  HttpRequestData,
} from '@recruitee/http';
import {
  LOCALE,
  PHRASE_APP_TRANSLATIONS,
  PhraseAppTranslations,
  phraseAppTranslationsLoader,
} from '@recruitee/i18n';
import {
  IntegrationsApiService,
  IntegrationsHttpClient,
  IntegrationsHttpRequestData,
} from '@recruitee/integrations-http';
import { INTEGRATIONS_FEATURE_FLAGS, IntegrationsCredentials } from '@recruitee/integrations-types';
import { uiTranslations } from '@recruitee/user-interface/locales';

import { RecruiteeAuthStrategy } from '../recruitee-auth-strategy';
import { IntegrationsRequestData } from './app/config/integrations-request-data';
import { RecruiteeRequestData } from './app/config/recruitee-request-data';
import { appTranslations, rtiTranslations, wysiwygTranslations } from './app/locales/app-locales';
import { loadAngularTranslations } from './app/locales/ng-locales';
import { WebappHttpApiErrorHandlerService } from './app/main/http-api-error-handler.service';
import { AppEnvironment } from './environments/interface';

export type Providers =
  | ValueProvider
  | ClassProvider
  | ConstructorProvider
  | ExistingProvider
  | FactoryProvider;

const hotModule = (<any>import.meta).webpackHot;

interface FeaturesData {
  devFlags: DevFlagsMap;
  featureSpec: FeatureSpec[];
}

interface HMRCache {
  store?: any;
  authToken?: string;
  preloaded?: { admin: CurrentUser; companyId: number | null; locale: string };
  preloadedNext?: [
    phraseAppTranslations: PhraseAppTranslations[],
    featuresData: FeaturesData,
    ...temp: any[],
  ];
}

const NO_COMPANY_PATH_WHITELIST = [
  '/admin',
  '/secure_change/token',
  '/secure_change/email_code',
  // create company
  '/companies',
  // delete account
  '/deletion_requests',
];

@Injectable()
export class WebAppBootstrap {
  private authMethod = 'Bearer';
  private currentInjector = new BehaviorSubject(this.initialInjector);
  private forwardProviders: Providers[] = [];
  private isDOMContentLoaded = new Promise(resolve =>
    document.addEventListener('DOMContentLoaded', resolve, false),
  );
  private onDestroy$ = new Subject<void>();
  private hmrCache: HMRCache = hotModule?.data || {};

  private startupZone = Zone.current.fork({
    name: 'bootstrap startup requests',
    properties: {
      startup: true,
    },
  });

  constructor(
    private authService: AuthService,
    @Inject(AuthorisationStrategy) private authStrategy: RecruiteeAuthStrategy,
    private initialInjector: Injector,
    @Inject(APP_CONFIG) private appConfig: IAppConfig,
    private genericAuthService: GenericAuthService<TellentAuthTokenResponse, TellentUserResponse>,
  ) {
    if (hotModule) {
      this.handleHmr();
    }
  }

  public async bootstrap(forwardProviders: Providers[], rootModule: Promise<Type<any>>) {
    this.forwardProviders = [...this.forwardProviders, ...forwardProviders];
    await this.beforeAuth();
    const { value: injector } = this.currentInjector;
    const authToken =
      injector.get(AUTH_TOKEN, null, { optional: true }) ??
      (await this.getFromHmrCache(
        'authToken',
        async () => (await this.startupZone.run(() => this.authorize()))!,
      ));
    if (!authToken) return;

    const {
      translations,
      integrationsFeatureFlags,
      featuresData: { devFlags, featureSpec },
    }: Awaited<ReturnType<WebAppBootstrap['preloadData']>> = await this.startupZone.run(() =>
      this.preloadData(authToken),
    );
    const ngRootModule = await rootModule;

    const providers = [
      ...this.getProviders(),
      { provide: PHRASE_APP_TRANSLATIONS, useValue: translations },
      { provide: DEV_FLAGS, useValue: devFlags },
      { provide: INTEGRATIONS_FEATURE_FLAGS, useValue: integrationsFeatureFlags },
      { provide: FEATURE_SPEC, useValue: featureSpec },
      ...(this.hmrCache.store ? [{ provide: INITIAL_STATE, useValue: this.hmrCache.store }] : []),
    ];
    const rootModuleRef = await platformBrowserDynamic(providers).bootstrapModule(ngRootModule, {
      ngZone: 'noop',
    });
    const store = rootModuleRef.injector.get(Store);
    store.pipe(takeUntil(this.onDestroy$)).subscribe(value => (this.hmrCache.store = value));
  }

  private beforeAuth() {
    return Promise.all([
      document.readyState === 'complete' ? Promise.resolve() : this.isDOMContentLoaded,
    ]);
  }

  private async authorize(): Promise<string | null> {
    const authToken = await this.authService
      .getAccessToken()
      .catch(err => console.error(`~~~~~~ getAccessToken ERROR ~~~~~~\n`, err));
    if (!authToken) {
      this.authStrategy.onAuthorizationFailed();
      return null;
    }
    this.authService.saveAccessTokenInCookie(authToken, this.appConfig.hasHttps);
    return authToken;
  }

  // eslint-disable-next-line max-lines-per-function
  private async preloadData(authToken: string) {
    const environment = <AppEnvironment>this.currentInjector.value.get(ENVIRONMENT);

    let tellentCurrentUser;

    const { admin, companyId, locale } = await this.getFromHmrCache('preloaded', () =>
      this.authService.fetchAdmin(
        authToken,
        this.appConfig.apiUrl,
        this.authMethod,
        RecruiteeRequestData.getIdentityHeaders(environment, this.appConfig.isE2e),
      ),
    );

    const { tsEnabled, appGuid } = this.resolveCurrentCompany(admin, companyId!);

    /*
     Until all users are migrated and logged on with new tellent auth,
     there might be a situation where the endpoint responds with an error due to an invalid token.

     To be removed after one login project release.
   */

    if (tsEnabled) {
      try {
        const tellentUserResponse = await this.genericAuthService.fetchUser(
          authToken,
          environment.tellentApiUrl + '/api/me',
        );
        tellentCurrentUser = tellentUserResponse.user;
      } catch {}
    }

    const orgGuid =
      !!tellentCurrentUser && appGuid ? this.resolveOrgGuid(appGuid, tellentCurrentUser) : null;

    this.updateInjector([
      { provide: AUTH_TOKEN, useValue: authToken },
      { provide: COMPANY_ID, useValue: companyId },
      { provide: AUTH_METHOD, useValue: this.authMethod },
      { provide: LOCALE, useValue: locale },
      { provide: LOCALE_ID, useValue: locale },
      { provide: ADMIN, useValue: admin },
      { provide: ORG_GUID, useValue: orgGuid },
      { provide: TELLENT_SUITE_ENABLED, useValue: tsEnabled },
      { provide: TELLENT_CURRENT_USER, useValue: tellentCurrentUser || {} },
      { provide: ANALYTICS_DISABLED, useValue: this.authService.isSaSoftLogin },
      { provide: DevFlagsHttpService },
      { provide: HttpApiService },
      { provide: WebappHttpApiErrorHandlerService },
      { provide: HttpRequestData, useClass: RecruiteeRequestData },
      { provide: IntegrationsHttpRequestData, useClass: IntegrationsRequestData },
      { provide: HttpApiErrorHandlerService, useExisting: WebappHttpApiErrorHandlerService },
      { provide: PATH_WHITELIST, useValue: companyId ? null : NO_COMPANY_PATH_WHITELIST },
      { provide: IntegrationsApiService },
      { provide: IntegrationsCredentials },
      { provide: IntegrationsHttpClient },
      { provide: FeatureSpecHttpClient },
    ]);

    const integrationsHttpClient = this.currentInjector.value.get(IntegrationsHttpClient);
    const features = lastValueFrom(this.loadFeatures(companyId));
    const integrationsFeatureFlagsData = companyId
      ? lastValueFrom(integrationsHttpClient.loadFeatureFlags())
      : Promise.resolve().then(() => ({}));

    const [translations, featuresData, integrationsFeatureFlags] = await this.getFromHmrCache(
      'preloadedNext',
      () =>
        Promise.all([
          this.loadTranslations(locale),
          features,
          integrationsFeatureFlagsData.catch(() => ({})),
          loadAngularTranslations(locale).catch(err => console.error(err)),
        ]),
    );

    return { translations, integrationsFeatureFlags, featuresData };
  }

  private loadFeatures(companyId: number | null) {
    const devFlagsHttpService = this.currentInjector.value.get(DevFlagsHttpService);
    const featureSpecHttpClient = this.currentInjector.value.get(FeatureSpecHttpClient);

    if (!companyId) {
      return of({
        devFlags: <DevFlagsMap>{},
        featureSpec: <FeatureSpec[]>[],
      });
    }

    return forkJoin({
      devFlags: devFlagsHttpService.fetch(),
      featureSpec: featureSpecHttpClient.fetch().pipe(
        map(response => response.features),
        catchError(error => {
          console.error(error);
          return of(<FeatureSpec[]>[]);
        }),
      ),
    }).pipe(
      take(1),
      catchError(error => {
        console.error(error);
        return of({
          devFlags: <DevFlagsMap>{},
          featureSpec: <FeatureSpec[]>[],
        });
      }),
    );
  }

  private loadTranslations(locale: string) {
    return phraseAppTranslationsLoader(locale, 'en', [
      appTranslations,
      uiTranslations,
      rtiTranslations,
      wysiwygTranslations,
    ]);
  }

  private getProviders(): ValueProvider[] {
    const { value: injector } = this.currentInjector;
    return this.forwardProviders.reduce(
      (acc: ValueProvider[], { provide }) => [...acc, { provide, useValue: injector.get(provide) }],
      [],
    );
  }

  private updateInjector(providers: Providers[]) {
    this.forwardProviders = [...this.forwardProviders, ...providers];
    const { value: injector } = this.currentInjector;
    this.currentInjector.next(Injector.create({ providers, parent: injector }));
  }

  private handleHmr() {
    const statusHandler = (status: string) => {
      if (status === 'check') {
        hotModule.invalidate();
        hotModule.removeStatusHandler(statusHandler);
      }
    };
    hotModule.status(statusHandler);
    hotModule.dispose((data: any) => {
      if (!data) return;

      this.onDestroy$.next();
      Object.assign(data, this.hmrCache);
    });
  }

  private async getFromHmrCache<
    // eslint-disable-next-line max-lines
    K extends keyof HMRCache,
    T extends () => Promise<Exclude<HMRCache[K], null | undefined>>,
  >(key: K, fn: T): Promise<ReturnType<T>> {
    if (this.hmrCache[key]) return this.hmrCache[key];

    const value = await fn();
    this.hmrCache = {
      ...this.hmrCache,
      [key]: value,
    };
    return value;
  }

  private resolveOrgGuid(appGuid: string, user: TellentCurrentUser): TellentMember['orgGuid'] {
    return resolveMemberOrgGuid(user, appGuid);
  }

  private resolveCurrentCompany(admin: CurrentUser, companyId: number) {
    return admin.companies.find(c => c.id === companyId) || { tsEnabled: false, appGuid: null };
  }
}
